import type { Facets } from '@iheartjane/dm-sdk';
import {
  expandFeelings,
  hasJaneDefinedRowEnabled,
  isEffectsDisabledState,
} from '@iheartjane/menu';
import { isProductKind } from '@iheartjane/types';
import type { LabeledOption, Nullable, Nullish } from '@iheartjane/types';
import some from 'lodash-es/some';
import uniqBy from 'lodash-es/uniqBy';

import {
  WEIGHT_LABELS,
  WEIGHT_TO_GRAMS,
} from '@jane/bloom/dataAccess/constants';
import {
  PRICE_BUCKET_FILTER_ITEMS,
  RATING_FILTER_ITEMS,
} from '@jane/bloom/dataAccess/dm/constants';
import type { Facet } from '@jane/bloom/types/facet';
import {
  SortOptions,
  SortOptionsSchema,
} from '@jane/bloom/types/schemas/algolia/product';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  SpecialFilterRules,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { CustomRow } from '@jane/bloom/types/schemas/roots/bloom/customRow';
import type {
  BloomMenuStore,
  VisibleKindSubTypes,
} from '@jane/bloom/types/schemas/roots/bloom/menu';
import type { NavigationCategory } from '@jane/bloom/types/schemas/roots/product';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import {
  CURRENT_PAGE_QUERY_KEY,
  SEARCH_TERM_QUERY_KEY,
  SORT_ORDER_QUERY_KEY,
} from '@jane/bloom/utils/queryParams/queryParams';
import { getCustomSortedCategories } from '@jane/bloom/utils/sorting/getCustomSortedCategories';
import { getDefaultSortedCategories } from '@jane/bloom/utils/sorting/getDefaultSortedCategories';
import { formatLabel } from '@jane/bloom/utils/strings/formatLabel';
import {
  blendFacet,
  getUpdatedCountsPrice,
  getUpdatedCountsRating,
} from '@jane/bloom/viewModels/filters/utils';
import type {
  ProductSearchCriteria,
  ProductSearchCriteriaDefaults,
} from '@jane/bloom/viewModels/productSearch';
import type {
  ProductSearchFilterCategory,
  ProductSearchFilterFacet,
  ProductSearchFiltersViewModel,
} from '@jane/bloom/viewModels/productSearchFilters';

const SALE_ROW_TYPE = 'sale';

type BuildCategoriesType = {
  /**
   * A record of DM facets containing product counts across subtypes - either/both `kind_root_subtype` and/or `kind_brand_subtype`.
   * @example
   * {
   *   'kind_root_subtype': {
   *     'flower:Flower': 39,
   *     'flower:Pre Pack': 1,
   *     'flower:Premium': 6,
   *     'flower:Smalls': 22,
   *   },
   *   'kind_brand_subtype': {
   *     'flower:1/8th': 32,
   *     'flower:1/8th Flower Jar': 1,
   *     'flower:Essence Smalls': 4,
   *     'flower:Popcorn': 5,
   *     'flower:Pre-Packed Flower': 1,
   *     'flower:Premium Flower': 6,
   *     'flower:Reserve': 1,
   *     'flower:Reserve Smalls': 2,
   *     'flower:Smalls': 11,
   *   },
   * }
   */
  kindSubTypeFacets?: Record<string, Facet>;
  productTypeLabels: { [key: string]: string };
  productTypeRanking?: Nullable<string[]>;
  /**
   * A DM facet containing product counts across root types - both for `kind` and certain standard rows.
   * @example
   * {
   *   best_selling: 8,
   *   edible: 24,
   *   extract: 15,
   *   flower: 104,
   *   gear: 150,
   *   sale: 135,
   * }
   */
  rootTypeFacet?: Facet;
  storeId: number;
  visibleKindSubTypes?: VisibleKindSubTypes | null;
};

/** Assembles categories and subcategories for use in filtering. */
export function buildCategories({
  kindSubTypeFacets = {},
  productTypeLabels,
  productTypeRanking,
  rootTypeFacet = {},
  storeId,
  visibleKindSubTypes,
}: BuildCategoriesType) {
  const rootTypes = Object.entries(rootTypeFacet).filter(([key]) =>
    isProductKind(key)
  );

  const buildSubCategories = (categoryName: string) =>
    uniqBy(
      Object.entries(kindSubTypeFacets)
        .flatMap(([kindSubType, subTypeFacet]) =>
          Object.entries(subTypeFacet ?? {}).map(([subType, count]) => ({
            count,
            kindSubType,
            subType,
          }))
        )
        .filter(({ kindSubType }) => {
          const visibleKindSubTypesForCategory: string[] =
            visibleKindSubTypes?.[categoryName] ?? ['kind_root_subtype'];
          return visibleKindSubTypesForCategory.includes(kindSubType);
        })
        .filter(({ subType }) => subType.startsWith(`${categoryName}:`))
        .map(({ count, subType }) => {
          const subCategory = subType.replace(`${categoryName}:`, '');
          const subCategoryCustomLabel =
            productTypeLabels[subCategory.toLowerCase()];

          return {
            count: +count,
            label: formatLabel(subCategoryCustomLabel || subCategory),
            value: subType,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
      'value'
    );

  const categories: ProductSearchFilterCategory[] = rootTypes.map(
    ([value, count]) => {
      const customRowDisplayName = productTypeLabels[value] ?? value;

      return {
        count: +count,
        label: formatLabel(
          customRowDisplayName.replace(new RegExp(`(-${storeId}$)`), '') // removes `-${storeId}` at the end of a custom category name
        ),
        subCategories: buildSubCategories(value),
        value,
      };
    }
  );

  const sortedCategories = isEmpty(productTypeRanking)
    ? getDefaultSortedCategories({ categories })
    : getCustomSortedCategories({ categories, productTypeRanking });

  const customCategoryLabels: Record<string, string> = {};
  sortedCategories.forEach((catObj: LabeledOption) => {
    if (catObj.value === 'best_selling') {
      customCategoryLabels['best-selling'] = 'Best sellers';
    } else {
      customCategoryLabels[catObj.value] = catObj.label;
    }
  });

  return { categories: sortedCategories, customCategoryLabels };
}

interface BuildSaleFilterArgs {
  customRows?: CustomRow[];
  productTypeLabels: { [key: string]: string };
  rootTypeFacet?: Facet;
}

/**
 * Builds the sale item for the 'Deals and rewards' section. Sale is a Jane
 * defined row rather than a product category, so it is offered there instead of
 * with the categories, and only when the store has the row enabled.
 *
 * Mirrors `buildSaleFilterItems` in `@jane/search/util`, which Bloom cannot
 * import; the two share the row lookup via `hasJaneDefinedRowEnabled`.
 */
export function buildSaleFilter({
  customRows,
  productTypeLabels,
  rootTypeFacet = {},
}: BuildSaleFilterArgs): ProductSearchFilterFacet[] {
  const saleCount = Number(rootTypeFacet[SALE_ROW_TYPE] ?? 0);

  if (!saleCount || !hasJaneDefinedRowEnabled(customRows, SALE_ROW_TYPE)) {
    return [];
  }

  return [
    {
      count: saleCount,
      label: formatLabel(productTypeLabels[SALE_ROW_TYPE] || SALE_ROW_TYPE),
      value: SALE_ROW_TYPE,
    },
  ];
}

export function buildWeightsFilter(
  facet: Facet = {},
  specialRules?: SpecialFilterRules
) {
  const entries = Object.entries(facet);

  let filters = entries
    .filter(([value]) => WEIGHT_LABELS[value])
    .map(([value, count]) => ({
      amount: Number(WEIGHT_LABELS[value].slice(0, -1)),
      count,
      label: formatLabel(WEIGHT_LABELS[value].toLowerCase() || value),
      value,
    }));
  const bulkWeights = specialRules?.bulk_pricing?.includes?.[0]?.bulk_weights;
  if (bulkWeights && bulkWeights.length > 0) {
    filters = filters.filter((filter) =>
      bulkWeights.some(
        (weight) =>
          WEIGHT_TO_GRAMS[weight.price_id] === WEIGHT_TO_GRAMS[filter.value]
      )
    );
  }

  return filters.sort((a, b) => (a.amount > b.amount ? 1 : -1));
}

export function buildSpecialsFilter(
  storeSpecials: StoreSpecials = [],
  applicableSpecialIdsFacet: Facet = {}
) {
  const applicableSpecialIds = Object.keys(applicableSpecialIdsFacet);

  return storeSpecials
    .filter((storeSpecial) =>
      applicableSpecialIds.includes(storeSpecial.id.toString())
    )
    .map((special) => ({
      count: applicableSpecialIdsFacet[special.id] || 0,
      label: formatLabel(special.title),
      value: special.id.toString(),
    }));
}

function buildRatingFilter(facet?: Facet) {
  return getUpdatedCountsRating(RATING_FILTER_ITEMS, facet);
}

function buildPriceFilter(facet?: Facet) {
  return getUpdatedCountsPrice(PRICE_BUCKET_FILTER_ITEMS, facet);
}

interface BuildFilterArgs {
  disabledFeelingActivityTags?: Nullable<string[]>;
  facet?: Facet;
  lineageLabels?: Record<string, string> | null;
  preserveCase?: boolean;
  sort?: boolean;
}

export function buildFilter({
  disabledFeelingActivityTags,
  facet = {},
  lineageLabels,
  preserveCase,
  sort,
}: BuildFilterArgs) {
  const allEntries = Object.entries(facet);
  const entries = allEntries
    .filter(([value]) => !disabledFeelingActivityTags?.includes(value))
    .map(([value, count]) => ({
      count,
      label: formatLabel(lineageLabels?.[value] ?? value, {
        preserveCase,
        uppercaseAcronyms: ['CBD', 'THC'],
      }),
      value,
    }));

  return sort
    ? entries.sort((a, b) =>
        a.label
          .toLocaleLowerCase()
          .localeCompare(b.label.toLocaleLowerCase(), undefined, {
            numeric: true,
          })
      )
    : entries;
}

type ParseSearchCriteriaFromRequestProps = {
  navigationCategory?: NavigationCategory;
  requestUrl: string;
  specialIds?: string[];
};

const searchCriteriaDefaults: ProductSearchCriteriaDefaults = {
  hitsPerPage: 15,
};

export function parseSearchCriteriaFromRequest({
  navigationCategory = 'shop-all',
  requestUrl,
  specialIds = [],
}: ParseSearchCriteriaFromRequestProps): ProductSearchCriteria {
  const searchParams = new URL(requestUrl).searchParams;

  let cbdPotenciesMinVal: string | null,
    cbdPotenciesMaxVal: string | null,
    thcPotenciesMinVal: string | null,
    thcPotenciesMaxVal: string | null;

  if (searchParams.has('cbdPotency')) {
    const split = searchParams.get('cbdPotency')?.split(':') || [];

    if (split.length > 1) {
      cbdPotenciesMinVal = decodeURI(split[0]);
      cbdPotenciesMaxVal = decodeURI(split[1]);
    } else {
      cbdPotenciesMinVal = searchParams.get('cbdPotenciesMin');
      cbdPotenciesMaxVal = searchParams.get('cbdPotenciesMax');
    }
  } else {
    cbdPotenciesMinVal = searchParams.get('cbdPotenciesMin');
    cbdPotenciesMaxVal = searchParams.get('cbdPotenciesMax');
  }

  if (searchParams.has('thcPotency')) {
    const split = searchParams.get('thcPotency')?.split(':') || [];

    if (split.length > 1) {
      thcPotenciesMinVal = decodeURI(split[0]);
      thcPotenciesMaxVal = decodeURI(split[1]);
    } else {
      thcPotenciesMinVal = searchParams.get('thcPotenciesMin');
      thcPotenciesMaxVal = searchParams.get('thcPotenciesMax');
    }
  } else {
    thcPotenciesMinVal = searchParams.get('thcPotenciesMin');
    thcPotenciesMaxVal = searchParams.get('thcPotenciesMax');
  }

  const getSearchParam = (singular: string, plural: string) => {
    return searchParams.has(plural)
      ? searchParams.get(plural)?.split(',')
      : searchParams.getAll(singular);
  };

  const hitsPerPageParam = searchParams.get('hitsPerPage');
  const currentPageParam = searchParams.get(CURRENT_PAGE_QUERY_KEY);
  const categories = getSearchParam('category', 'categories');
  // Expanded here rather than where the URL is read, so the selected-filter tags
  // still show the one value the shopper picked.
  const feelings = getSearchParam('feeling', 'feelings');

  let sort: SortOptions = SortOptions.recommended;

  if (searchParams.get(SORT_ORDER_QUERY_KEY)) {
    sort =
      SortOptionsSchema.safeParse(searchParams.get(SORT_ORDER_QUERY_KEY))
        .data ?? SortOptions.recommended;
  }

  return {
    activities: getSearchParam('activity', 'activities'),
    brands: getSearchParam('brand', 'brands'),
    categories: categories?.filter(
      (category) =>
        category !== 'shop-all' &&
        category !== 'featured' &&
        category !== 'jane-gold'
    ),
    cbdPotenciesMax: cbdPotenciesMaxVal
      ? Number(cbdPotenciesMaxVal)
      : undefined,
    cbdPotenciesMin: cbdPotenciesMinVal
      ? Number(cbdPotenciesMinVal)
      : undefined,
    currentPage: currentPageParam ? Number(currentPageParam) : undefined,
    feelings: feelings ? expandFeelings(feelings) : undefined,
    hasBrandDiscount:
      searchParams.has('hasBrandDiscount') ||
      navigationCategory === 'jane-gold',
    hitsPerPage: hitsPerPageParam
      ? Number(hitsPerPageParam)
      : searchCriteriaDefaults.hitsPerPage,
    lineages: getSearchParam('lineage', 'lineages'),
    prices: getSearchParam('price', 'prices'),
    ratings: getSearchParam('rating', 'ratings'),
    searchText: searchParams.get(SEARCH_TERM_QUERY_KEY) ?? undefined,
    selectedCategory: ['shop-all', 'deals', 'jane-gold'].includes(
      navigationCategory
    )
      ? undefined
      : navigationCategory,
    sort,
    specialIds:
      navigationCategory === 'deals'
        ? specialIds
        : getSearchParam('special', 'specials'),
    specialType:
      navigationCategory === 'deals'
        ? /** Deals PLP includes only product specials. Criteria should be overridden when viewing a particular special. */
          'product'
        : undefined,
    terpenes: getSearchParam('terpene', 'terpenes'),
    thcPotenciesMax: thcPotenciesMaxVal
      ? parseInt(thcPotenciesMaxVal)
      : undefined,
    thcPotenciesMin: thcPotenciesMinVal
      ? parseInt(thcPotenciesMinVal)
      : undefined,
    weights: getSearchParam('weight', 'weights'),
  };
}

export interface BuildProductSearchFiltersViewModelArgs {
  baselineFacets: Facets;
  brandSpecialsFacets?: Nullish<Facets>;
  filteredFacets?: Facets;
  frozenFacetsStateClient?: Nullish<Facets>;
  presentationOptions?: {
    customLanguage?: CustomLanguage;
    pinnedStore?: BloomMenuStore;
    storeSpecials?: StoreSpecials;
    visibleKindSubTypes?: VisibleKindSubTypes | null;
  };
  searchCriteria?: ProductSearchCriteria;
  setLocalStorageCallback?: (
    facetGroup: string,
    facetData: NonNullable<Facet>
  ) => void;
  specialRules?: SpecialFilterRules;
}

export function buildProductSearchFiltersViewModel({
  baselineFacets,
  brandSpecialsFacets,
  filteredFacets = {},
  frozenFacetsStateClient,
  presentationOptions = {},
  searchCriteria,
  setLocalStorageCallback,
  specialRules,
}: BuildProductSearchFiltersViewModelArgs): ProductSearchFiltersViewModel {
  const { customLanguage, pinnedStore, storeSpecials, visibleKindSubTypes } =
    presentationOptions;
  const reviewsDisabled = pinnedStore?.product_reviews_enabled === false;
  const hideEffects =
    reviewsDisabled || isEffectsDisabledState(pinnedStore?.state);

  // Blended once and shared: `blendFacet` writes through to local storage, so
  // calling it twice for the same facet group would double up that side effect.
  const rootTypeFacet = blendFacet({
    baselineFacet: baselineFacets['root_types'],
    currentFacetGroup: 'root_types',
    filteredFacet: filteredFacets['root_types'],
    frozenFacetStateClient: frozenFacetsStateClient?.['root_types'],
    isFilteringThisFacet: some(searchCriteria?.categories),
    setLocalStorageCallback,
  });

  return {
    activities: hideEffects
      ? []
      : buildFilter({
          disabledFeelingActivityTags:
            pinnedStore?.disabled_feeling_and_activity_tags,
          facet: blendFacet({
            baselineFacet: baselineFacets['activities'],
            currentFacetGroup: 'activities',
            filteredFacet: filteredFacets['activities'],
            frozenFacetStateClient: frozenFacetsStateClient?.['activities'],
            isFilteringThisFacet: some(searchCriteria?.activities),
            setLocalStorageCallback,
          }),
        }),
    brands: buildFilter({
      facet: blendFacet({
        baselineFacet: baselineFacets['brand'],
        currentFacetGroup: 'brand',
        filteredFacet: filteredFacets['brand'],
        frozenFacetStateClient: frozenFacetsStateClient?.['brand'],
        isFilteringThisFacet: some(searchCriteria?.brands),
        setLocalStorageCallback,
      }),
      preserveCase: true,
      sort: true,
    }),
    brandSpecials: buildFilter({
      facet: blendFacet({
        // this is brandSpecialsFacets rather than baselineFacets because baselineFacet can include extra items
        // (e.g. items that don't account for segmentation or filters that are applied and not considered in searchCriteria - like in category pages)
        baselineFacet: brandSpecialsFacets?.['has_brand_discount'],
        currentFacetGroup: 'has_brand_discount',
        filteredFacet: brandSpecialsFacets?.['has_brand_discount'],
        frozenFacetStateClient: frozenFacetsStateClient?.['has_brand_discount'],
        isFilteringThisFacet: searchCriteria?.hasBrandDiscount,
        setLocalStorageCallback,
      }),
    }),
    categories: buildCategories({
      kindSubTypeFacets: {
        kind_brand_subtype: blendFacet({
          baselineFacet: baselineFacets['kind_brand_subtype'],
          currentFacetGroup: 'kind_brand_subtype',
          filteredFacet: filteredFacets['kind_brand_subtype'],
          frozenFacetStateClient:
            frozenFacetsStateClient?.['kind_brand_subtype'],
          isFilteringThisFacet: some(searchCriteria?.categories),
          setLocalStorageCallback,
        }),
        kind_root_subtype: blendFacet({
          baselineFacet: baselineFacets['kind_root_subtype'],
          currentFacetGroup: 'kind_root_subtype',
          filteredFacet: filteredFacets['kind_root_subtype'],
          frozenFacetStateClient:
            frozenFacetsStateClient?.['kind_root_subtype'],
          isFilteringThisFacet: some(searchCriteria?.categories),
          setLocalStorageCallback,
        }),
      },
      productTypeLabels: customLanguage?.productTypeLabels || {},
      productTypeRanking: pinnedStore?.custom_product_type_ranking,
      rootTypeFacet,
      storeId: pinnedStore?.id || 0,
      visibleKindSubTypes,
    }).categories,
    cbdPotencies: buildFilter({
      facet: blendFacet({
        baselineFacet: baselineFacets['percent_cbd'],
        currentFacetGroup: 'percent_cbd',
        filteredFacet: filteredFacets['percent_cbd'],
        frozenFacetStateClient: frozenFacetsStateClient?.['percent_cbd'],
        isFilteringThisFacet: Boolean(
          searchCriteria?.cbdPotenciesMax || searchCriteria?.cbdPotenciesMin
        ),
        setLocalStorageCallback,
      }),
    }),
    feelings: hideEffects
      ? []
      : buildFilter({
          disabledFeelingActivityTags:
            pinnedStore?.disabled_feeling_and_activity_tags,
          facet: blendFacet({
            baselineFacet: baselineFacets['feelings'],
            currentFacetGroup: 'feelings',
            filteredFacet: filteredFacets['feelings'],
            frozenFacetStateClient: frozenFacetsStateClient?.['feelings'],
            isFilteringThisFacet: some(searchCriteria?.feelings),
            setLocalStorageCallback,
          }),
        }),
    lineages: buildFilter({
      facet: blendFacet({
        baselineFacet: baselineFacets['category'],
        currentFacetGroup: 'category',
        filteredFacet: filteredFacets['category'],
        frozenFacetStateClient: frozenFacetsStateClient?.['category'],
        isFilteringThisFacet: some(searchCriteria?.lineages),
        setLocalStorageCallback,
      }),
      lineageLabels: customLanguage?.lineageLabels,
    }),
    prices: buildPriceFilter(
      blendFacet({
        baselineFacet: baselineFacets['bucket_price'],
        currentFacetGroup: 'bucket_price',
        filteredFacet: filteredFacets['bucket_price'],
        frozenFacetStateClient: frozenFacetsStateClient?.['bucket_price'],
        isFilteringThisFacet: some(searchCriteria?.prices),
        setLocalStorageCallback,
      })
    ),
    ratings: reviewsDisabled
      ? []
      : buildRatingFilter(
          blendFacet({
            baselineFacet: baselineFacets['aggregate_rating'],
            currentFacetGroup: 'aggregate_rating',
            filteredFacet: filteredFacets['aggregate_rating'],
            frozenFacetStateClient:
              frozenFacetsStateClient?.['aggregate_rating'],
            isFilteringThisFacet: some(searchCriteria?.ratings),
            setLocalStorageCallback,
          })
        ),
    sale: buildSaleFilter({
      customRows: pinnedStore?.custom_rows,
      productTypeLabels: customLanguage?.productTypeLabels || {},
      rootTypeFacet,
    }),
    specials: buildSpecialsFilter(
      storeSpecials,
      blendFacet({
        baselineFacet: baselineFacets['applicable_special_ids'],
        currentFacetGroup: 'applicable_special_ids',
        filteredFacet: filteredFacets['applicable_special_ids'],
        frozenFacetStateClient:
          frozenFacetsStateClient?.['applicable_special_ids'],
        isFilteringThisFacet: some(searchCriteria?.specialIds),
        setLocalStorageCallback,
      })
    ),
    terpenes: buildFilter({
      facet: blendFacet({
        baselineFacet: baselineFacets['compound_names'],
        currentFacetGroup: 'compound_names',
        filteredFacet: filteredFacets['compound_names'],
        frozenFacetStateClient: frozenFacetsStateClient?.['compound_names'],
        isFilteringThisFacet: some(searchCriteria?.terpenes),
        setLocalStorageCallback,
      }),
    }),
    thcPotencies: buildFilter({
      facet: blendFacet({
        baselineFacet: baselineFacets['percent_thc'],
        currentFacetGroup: 'percent_thc',
        filteredFacet: filteredFacets['percent_thc'],
        frozenFacetStateClient: frozenFacetsStateClient?.['percent_thc'],
        isFilteringThisFacet: Boolean(
          searchCriteria?.thcPotenciesMax || searchCriteria?.thcPotenciesMin
        ),
        setLocalStorageCallback,
      }),
    }),
    weights: buildWeightsFilter(
      blendFacet({
        baselineFacet: baselineFacets['available_weights'],
        currentFacetGroup: 'available_weights',
        filteredFacet: filteredFacets['available_weights'],
        frozenFacetStateClient: frozenFacetsStateClient?.['available_weights'],
        isFilteringThisFacet: some(searchCriteria?.weights),
        setLocalStorageCallback,
      }),
      specialRules
    ),
  };
}
